import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    LockClosedIcon,
    HomeIcon,
    UserIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';
import { FaGooglePlay, FaApple } from 'react-icons/fa';

import { loginSra } from '../services/api';

const fontStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&family=Roboto:wght@300;400;500&display=swap');
  .font-poppins { font-family: 'Poppins', sans-serif; }
  .font-roboto { font-family: 'Roboto', sans-serif; }
`;

const Login = () => {
    const navigate = useNavigate();
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [globalMessage, setGlobalMessage] = useState({ type: '', text: '' });

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setGlobalMessage({ type: '', text: '' });

        try {
            const payload = {
                email: identifier.trim(), 
                password: password.trim()
            };

            console.log("🚀 Attempting Login for:", identifier);

            // 1. API Call
            const data = await loginSra(payload);
            
            // --- THE TRUTH TEST ---
            // This alert will show you EXACTLY what the backend is sending.
            alert("BACKEND ROLE DETECTED: " + data.role);
            console.log("🔍 FULL SERVER RESPONSE:", data);

            // 2. Data Extraction with Normalization
            const token = data.access_token || data.token || "session_active";
            const empCode = data.emp_code || data.user_id || "N/A";
            const userName = data.user_name || "User";
            const employeeId = data.employee_id; // ✅ MUST be a number
            
            // Normalize role to lowercase
            let rawRole = data.role ? data.role.toString().toLowerCase().trim() : 'employee';
            
            // --- EMERGENCY OVERRIDE ---
            // This forces the app to treat you as a manager even if the backend fails.
            // REPLACE "manager@example.com" with your actual manager login email.
            if (identifier.trim().toLowerCase() === "bhuvankalyankumar@gmail.com") {
                console.warn("⚠️ FORCING MANAGER ROLE LOCALLY");
                rawRole = "manager";
            }
            
   localStorage.clear(); 

localStorage.setItem("access_token", token);
localStorage.setItem("emp_code", empCode);          // EMP8383
localStorage.setItem("role", rawRole.toUpperCase());
localStorage.setItem("userName", userName);

if (employeeId) {
  localStorage.setItem("employee_id", String(employeeId)); // ✅ numeric
}


            // 4. FORCE PASSWORD CHANGE CHECK
            if (data.must_change_password) {
                localStorage.setItem('userEmail', identifier.trim());
                navigate('/set-password');
                return; 
            }

            // 5. NAVIGATION
            console.log("🚦 Final Role determined for Navigation:", rawRole);

            if (rawRole === 'super_admin' || rawRole === 'admin') {
                navigate('/admin');
            } else if (rawRole === 'manager') {
                navigate('/dashboard');
            } else {
                navigate('/dashboard');
            }

        } catch (error) { 
            console.error("❌ Login Error:", error);
            const errorMessage = error.response?.data?.detail || "Invalid Credentials. Please try again.";
            setGlobalMessage({ type: 'error', text: errorMessage }); 
        } finally { 
            setIsLoading(false); 
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#f8fafc] font-roboto p-4 sm:p-6">
            <style>{fontStyles}</style>

            <Link 
                to="/" 
                className="fixed top-6 left-6 p-2 bg-white shadow-md rounded-full text-gray-600 hover:text-indigo-600 transition-all z-50 border border-gray-100"
            >
                <HomeIcon className="h-5 w-5" />
            </Link>

            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden min-h-[550px]"
            >
                {/* Banner Side */}
                <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-indigo-700 via-purple-700 to-indigo-900 p-12 text-white flex-col justify-between relative">
                    <div className="relative z-10">
                        <div className="h-10 w-10 bg-white/20 rounded-lg backdrop-blur-md mb-8 flex items-center justify-center">
                            <LockClosedIcon className="h-6 w-6 text-white" />
                        </div>
                        <h1 className="text-4xl font-bold font-poppins leading-tight mb-4">
                            GTPL <br /> Enterprise Portal
                        </h1>
                        <p className="text-indigo-100 text-sm leading-relaxed max-w-xs">
                            Securely access your HRMS dashboard, track attendance, and manage employee records.
                        </p>
                    </div>
                    <div className="relative z-10">
                        <div className="flex gap-3">
                            <div className="flex items-center bg-white/10 px-4 py-2 rounded-xl border border-white/10">
                                <FaGooglePlay className="mr-2 text-lg"/>
                                <span className="text-[10px] font-medium leading-tight">GET IT ON <br/><span className="text-xs font-bold">Google Play</span></span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Side */}
                <div className="w-full lg:w-1/2 p-8 sm:p-12 flex flex-col justify-center relative">
                    <div className="max-w-sm mx-auto w-full">
                        <div className="mb-10 text-center lg:text-left">
                            <h2 className="text-3xl font-bold font-poppins text-gray-800 mb-2">Sign In</h2>
                            <p className="text-gray-500 text-sm">Log in with your Manager credentials</p>
                        </div>

                        <form onSubmit={handleLoginSubmit} className="space-y-5">
                            <div className="relative group">
                                <UserIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-600 transition-colors" />
                                <input 
                                    type="email" 
                                    placeholder="Manager Email" 
                                    autoComplete="off"
                                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                                    value={identifier}
                                    onChange={(e) => setIdentifier(e.target.value)}
                                    required 
                                />
                            </div>

                            <div className="relative group">
                                <LockClosedIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-600 transition-colors" />
                                <input 
                                    type="password" 
                                    placeholder="Password" 
                                    autoComplete="new-password"
                                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required 
                                />
                            </div>

                            <button 
                                disabled={isLoading}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-indigo-200 transition-all flex items-center justify-center group disabled:opacity-70"
                            >
                                {isLoading ? "Processing..." : (
                                    <>
                                        Sign In
                                        <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                    </>
                                )}
                            </button>
                        </form>

                        <AnimatePresence>
                            {globalMessage.text && (
                                <motion.div 
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="mt-4 p-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-xs text-center"
                                >
                                    {globalMessage.text}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default Login;